import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { Loader2, Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { InlineTextareaField } from "@/components/inline-edit/inline-textarea-field";
import { InlineSwitchField } from "@/components/inline-edit/inline-switch-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { getSecurityDisplayName } from "@/lib/security-display-name";
import { SecurityOwnerFormModal } from "@/components/securities/security-owner-form-modal";
import { ValuationFormModal } from "@/components/securities/valuation-form-modal";
import { EncumbranceFormModal } from "@/components/securities/encumbrance-form-modal";
import { PropertyFormModal } from "@/components/securities/property-form-modal";
import { PropertyAddressSection } from "@/components/securities/property-address-section";
import { PropertyTitlesPanel } from "@/components/securities/property-titles-panel";
import { SecurityTenanciesTab } from "@/components/securities/security-tenancies-tab";
import { WaleDisplayCard } from "@/components/panels/wale-display-card";
import { EquipmentDetailTab } from "@/components/securities/equipment-detail-tab";
import { VehicleDetailTab } from "@/components/securities/vehicle-detail-tab";
import { FinancialAssetDetailTab } from "@/components/securities/financial-asset-detail-tab";
import { CashflowProjectionsPanel } from "@/components/securities/cashflow-projections-panel";

// Security types from schema (matches enum security_type_enum in database)
type SecurityType = "residential_property" | "commercial_property" | "vehicle" | "equipment" | "financial_asset";
// Security status from schema (matches enum security_status_enum - 9 values)
type SecurityStatus = "identified" | "proposed" | "valued" | "approved" | "registered" | "varied" | "substituted" | "discharged" | "cancelled";
// Valuation source from schema (matches enum valuation_source_enum - 12 values)
type ValuationSource = "avm_corelogic" | "avm_proptrack" | "avm_valocity" | "full_valuation" | "desktop_valuation" | "kerbside_valuation" | "contract_price" | "bank_valuation" | "self_assessed" | "redbook" | "glass_guide" | "manual_entry";

interface Security {
  id: string;
  securityType: SecurityType;
  collateralType: string | null;
  status: SecurityStatus;
  estimatedValue: string;
  valuationDate: string | null;
  valuationSource: ValuationSource | null;
  valuationConfidence: string | null;
  description: string | null;
  notes: string | null;
  isExistingSecurity: boolean;
  existingLenderId: string | null;
  existingLoanBalance: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SecurityOwner {
  id: string;
  securityId: string;
  ownerAccountId: string | null;
  ownerContactId: string | null;
  ownershipBasis: string;
  ownershipPercentage: string;
  notes: string | null;
}

interface SecurityValuation {
  id: string;
  securityId: string;
  valuationDate: string;
  valuationAmount: string;
  valuationSource: string;
  valuerId: string | null;
  notes: string | null;
}

interface SecurityEncumbrance {
  id: string;
  securityId: string;
  encumbranceType: string;
  encumbranceAmount: string | null;
  holderName: string | null;
  registrationDate: string | null;
  notes: string | null;
  // Discharge tracking fields
  isProposed: boolean | null;
  dischargeRequired: boolean | null;
  dischargeStatus: string | null;
  payoutFigure: string | null;
  payoutValidUntil: string | null;
  dischargeAuthoritySent: string | null;
  dischargeReceived: string | null;
}

interface SecurityProperty {
  id: string;
  securityId: string;
  isResidential: boolean;
  propertyTypeResidential: string | null;
  propertyTypeCommercial: string | null;
  propertyStatus: string | null;
  addressUnavailable: boolean | null;
  addressLine1: string | null;
  addressLine2: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  titleReference: string | null;
  lotPlan: string | null;
  landArea: string | null;
  buildingArea: string | null;
  yearBuilt: number | null;
  zoning: string | null;
  council: string | null;
}

const SECURITY_TYPE_OPTIONS = [
  { value: "residential_property", label: "Residential Property" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "vehicle", label: "Vehicle" },
  { value: "equipment", label: "Equipment" },
  { value: "financial_asset", label: "Financial Asset" },
];

const STATUS_OPTIONS = [
  { value: "identified", label: "Identified" },
  { value: "proposed", label: "Proposed" },
  { value: "valued", label: "Valued" },
  { value: "approved", label: "Approved" },
  { value: "registered", label: "Registered" },
  { value: "varied", label: "Varied" },
  { value: "substituted", label: "Substituted" },
  { value: "discharged", label: "Discharged" },
  { value: "cancelled", label: "Cancelled" },
];

const VALUATION_SOURCE_OPTIONS = [
  // AVM (Automated Valuation Model)
  { value: "avm_corelogic", label: "AVM - CoreLogic" },
  { value: "avm_proptrack", label: "AVM - PropTrack" },
  { value: "avm_valocity", label: "AVM - Valocity" },
  // Professional
  { value: "full_valuation", label: "Full Valuation" },
  { value: "desktop_valuation", label: "Desktop Valuation" },
  { value: "kerbside_valuation", label: "Kerbside Valuation" },
  { value: "bank_valuation", label: "Bank Valuation" },
  // Contract/Self
  { value: "contract_price", label: "Contract Price" },
  { value: "self_assessed", label: "Self Assessed" },
  { value: "manual_entry", label: "Manual Entry" },
  // Vehicle
  { value: "redbook", label: "Redbook" },
  { value: "glass_guide", label: "Glass Guide" },
];

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

// Discharge status badge colours:
// not_required: grey (secondary)
// discharge_requested: blue
// discharge_confirmed: amber
// discharge_received: green
// discharged: purple
const DISCHARGE_STATUS_BADGE_STYLES: Record<string, string> = {
  not_required: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  discharge_requested: "bg-blue-100 text-blue-800 hover:bg-blue-100/80",
  discharge_confirmed: "bg-amber-100 text-amber-800 hover:bg-amber-100/80",
  discharge_received: "bg-green-100 text-green-800 hover:bg-green-100/80",
  discharged: "bg-purple-100 text-purple-800 hover:bg-purple-100/80",
};

const DISCHARGE_STATUS_LABELS: Record<string, string> = {
  not_required: "Not Required",
  discharge_requested: "Discharge Requested",
  discharge_confirmed: "Discharge Confirmed",
  discharge_received: "Discharge Received",
  discharged: "Discharged",
};

function DischargeStatusBadge({ status, encumbranceId }: { status: string; encumbranceId: string }) {
  const className = DISCHARGE_STATUS_BADGE_STYLES[status] || "bg-secondary text-secondary-foreground";
  const label = DISCHARGE_STATUS_LABELS[status] || status;
  return (
    <Badge
      className={`text-xs ${className}`}
      data-testid={`discharge-badge-${encumbranceId}`}
      data-status={status}
    >
      {label}
    </Badge>
  );
}

function formatPercentage(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `${num.toFixed(2)}%`;
}

function humanizeSecurityType(securityType?: string | null): string {
  const cleaned = securityType?.trim();
  if (!cleaned) return "Security";
  return cleaned
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getSecurityDetailLabel(
  security: Pick<Security, "securityType" | "description">,
  propertyData?: Pick<SecurityProperty, "titleReference"> | null
): string {
  const displayName = getSecurityDisplayName({
    securityType: security.securityType,
    description: security.description,
    titleReference: propertyData?.titleReference ?? null,
  });

  if (displayName !== "-") {
    return displayName;
  }

  return `${humanizeSecurityType(security.securityType)} Security`;
}

export default function SecurityDetail() {
  const [, params] = useRoute("/securities/:id");
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useTabState("overview");

  // Modal states
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [valuationModalOpen, setValuationModalOpen] = useState(false);
  const [encumbranceModalOpen, setEncumbranceModalOpen] = useState(false);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);

  // Edit states - stores the item being edited
  const [editingOwner, setEditingOwner] = useState<SecurityOwner | null>(null);
  const [editingValuation, setEditingValuation] = useState<SecurityValuation | null>(null);
  const [editingEncumbrance, setEditingEncumbrance] = useState<SecurityEncumbrance | null>(null);

  // Delete confirmation states
  const [deleteOwnerDialog, setDeleteOwnerDialog] = useState<{ open: boolean; owner: SecurityOwner | null }>({ open: false, owner: null });
  const [deleteValuationDialog, setDeleteValuationDialog] = useState<{ open: boolean; valuation: SecurityValuation | null }>({ open: false, valuation: null });
  const [deleteEncumbranceDialog, setDeleteEncumbranceDialog] = useState<{ open: boolean; encumbrance: SecurityEncumbrance | null }>({ open: false, encumbrance: null });

  const securityId = params?.id;

  // Fetch security data
  const { data: security, isLoading } = useQuery<Security>({
    queryKey: [`/api/crm/securities/${securityId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}`);
      return response.json();
    },
    enabled: !!securityId,
  });

  // Fetch owners
  const { data: ownersData } = useQuery<SecurityOwner[]>({
    queryKey: [`/api/crm/securities/${securityId}/owners`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/owners`);
      return response.json();
    },
    enabled: !!securityId,
  });

  const owners = Array.isArray(ownersData) ? ownersData : [];

  // Fetch valuations
  const { data: valuationsData } = useQuery<SecurityValuation[]>({
    queryKey: [`/api/crm/securities/${securityId}/valuations`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/valuations`);
      return response.json();
    },
    enabled: !!securityId,
  });

  const valuations = Array.isArray(valuationsData) ? valuationsData : [];

  // Fetch encumbrances
  const { data: encumbrancesData } = useQuery<SecurityEncumbrance[]>({
    queryKey: [`/api/crm/securities/${securityId}/encumbrances`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/encumbrances`);
      return response.json();
    },
    enabled: !!securityId,
  });

  const encumbrances = Array.isArray(encumbrancesData) ? encumbrancesData : [];

  // Fetch property details (for property securities)
  const { data: propertyData, isLoading: isPropertyLoading } = useQuery<SecurityProperty>({
    queryKey: [`/api/crm/securities/${securityId}/property`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/property`);
      if (response.status === 404) {
        return null;
      }
      return response.json();
    },
    enabled: !!securityId && (security?.securityType === "residential_property" || security?.securityType === "commercial_property"),
  });

  // Prefetch WALE data for commercial properties (so it's ready when user clicks WALE tab)
  // Use short staleTime so data is refetched when switching back to WALE tab (for real-time updates)
  useQuery({
    queryKey: ["/api/crm/securities", securityId, "wale"],
    enabled: !!securityId && security?.securityType === "commercial_property",
    staleTime: 1000,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Security>) => {
      const response = await apiRequest("PATCH", `/api/crm/securities/${securityId}`, updates);
      if (!response.ok) {
        throw new Error("Failed to update security");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/securities"] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}`] });
      toast({ title: "Success", description: "Security updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleUpdate = async (field: string, value: any) => {
    await updateMutation.mutateAsync({ [field]: value });
  };

  // Delete mutations
  const deleteOwnerMutation = useMutation({
    mutationFn: async (ownerId: string) => {
      const response = await apiRequest("DELETE", `/api/crm/securities/${securityId}/owners/${ownerId}`);
      if (!response.ok) {
        throw new Error("Failed to delete owner");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}/owners`] });
      toast({ title: "Success", description: "Owner deleted successfully" });
      setDeleteOwnerDialog({ open: false, owner: null });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteValuationMutation = useMutation({
    mutationFn: async (valuationId: string) => {
      const response = await apiRequest("DELETE", `/api/crm/securities/${securityId}/valuations/${valuationId}`);
      if (!response.ok) {
        throw new Error("Failed to delete valuation");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}/valuations`] });
      toast({ title: "Success", description: "Valuation deleted successfully" });
      setDeleteValuationDialog({ open: false, valuation: null });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteEncumbranceMutation = useMutation({
    mutationFn: async (encumbranceId: string) => {
      const response = await apiRequest("DELETE", `/api/crm/securities/${securityId}/encumbrances/${encumbranceId}`);
      if (!response.ok) {
        throw new Error("Failed to delete encumbrance");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}/encumbrances`] });
      toast({ title: "Success", description: "Encumbrance deleted successfully" });
      setDeleteEncumbranceDialog({ open: false, encumbrance: null });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Edit handlers
  const handleEditOwner = (owner: SecurityOwner) => {
    setEditingOwner(owner);
    setOwnerModalOpen(true);
  };

  const handleEditValuation = (valuation: SecurityValuation) => {
    setEditingValuation(valuation);
    setValuationModalOpen(true);
  };

  const handleEditEncumbrance = (encumbrance: SecurityEncumbrance) => {
    setEditingEncumbrance(encumbrance);
    setEncumbranceModalOpen(true);
  };

  // Modal close handlers (reset editing state)
  const handleOwnerModalClose = (open: boolean) => {
    setOwnerModalOpen(open);
    if (!open) setEditingOwner(null);
  };

  const handleValuationModalClose = (open: boolean) => {
    setValuationModalOpen(open);
    if (!open) setEditingValuation(null);
  };

  const handleEncumbranceModalClose = (open: boolean) => {
    setEncumbranceModalOpen(open);
    if (!open) setEditingEncumbrance(null);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!security) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground">Security not found</p>
      </div>
    );
  }

  const securityLabel = getSecurityDetailLabel(security, propertyData);

  const getStatusBadgeColor = (status: SecurityStatus) => {
    switch (status) {
      case "identified":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      case "proposed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "valued":
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200";
      case "approved":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200";
      case "registered":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "varied":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
      case "substituted":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "discharged":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Check if this is a property security type
  const isPropertySecurity = security.securityType === "residential_property" || security.securityType === "commercial_property";

  // Build tabs dynamically based on security type
  const tabs = [
    {
      value: "overview",
      label: "Overview",
      content: (
        <div className="space-y-4">
          {/* Property Address Section - Only for property securities */}
          {isPropertySecurity && (
            <PropertyAddressSection
              securityId={securityId!}
              property={propertyData || null}
              isPropertyLoading={isPropertyLoading}
            />
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {/* Basic Information */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="mb-4 font-semibold">Basic Information</h3>
                <div className="grid grid-cols-2 gap-2">
                  <InlineSelectField
                    label="Security Type"
                    value={security.securityType}
                    options={SECURITY_TYPE_OPTIONS}
                    onSave={(value) => handleUpdate("securityType", value)}
                    disabled={updateMutation.isPending}
                  />
                  <InlineSelectField
                    label="Status"
                    value={security.status}
                    options={STATUS_OPTIONS}
                    onSave={(value) => handleUpdate("status", value)}
                    disabled={updateMutation.isPending}
                  />
                  <div className="col-span-2">
                    <InlineTextareaField
                      label="Description"
                      value={security.description || ""}
                      onSave={(value) => handleUpdate("description", value)}
                      disabled={updateMutation.isPending}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Valuation */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="mb-4 font-semibold">Valuation</h3>
                <div className="grid grid-cols-2 gap-2">
                  <InlineTextField
                    label="Estimated Value"
                    value={security.estimatedValue}
                    onSave={(value) => handleUpdate("estimatedValue", value)}
                    disabled={updateMutation.isPending}
                  />
                  <div className="flex flex-col space-y-1">
                    <span className="text-sm text-muted-foreground">Valuation Date</span>
                    <span className="text-sm">{formatDate(security.valuationDate)}</span>
                  </div>
                  <InlineSelectField
                    label="Valuation Source"
                    value={security.valuationSource || "manual_entry"}
                    options={VALUATION_SOURCE_OPTIONS}
                    onSave={(value) => handleUpdate("valuationSource", value)}
                    disabled={updateMutation.isPending}
                  />
                  <InlineTextField
                    label="Confidence"
                    value={security.valuationConfidence || ""}
                    onSave={(value) => handleUpdate("valuationConfidence", value)}
                    disabled={updateMutation.isPending}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Existing Security */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="mb-4 font-semibold">Existing Security</h3>
                <div className="grid grid-cols-2 gap-2">
                  <InlineSwitchField
                    label="Is Existing Security"
                    value={security.isExistingSecurity}
                    onSave={(value) => handleUpdate("isExistingSecurity", value)}
                    disabled={updateMutation.isPending}
                    testId="switch-existing-security"
                  />
                  {security.isExistingSecurity && (
                    <>
                      <div className="flex flex-col space-y-1">
                        <span className="text-sm text-muted-foreground">Existing Lender</span>
                        <span className="text-sm">{security.existingLenderId || "-"}</span>
                      </div>
                      <InlineTextField
                        label="Existing Loan Balance"
                        value={security.existingLoanBalance || ""}
                        onSave={(value) => handleUpdate("existingLoanBalance", value)}
                        disabled={updateMutation.isPending}
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="mb-4 font-semibold">Notes</h3>
                <div className="space-y-2">
                  <InlineTextareaField
                    label="Internal Notes"
                    value={security.notes || ""}
                    onSave={(value) => handleUpdate("notes", value)}
                    disabled={updateMutation.isPending}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ),
    },
    {
      value: "owners",
      label: `Owners (${owners.length})`,
      content: (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">Security Owners</h3>
              <Button size="sm" variant="outline" onClick={() => setOwnerModalOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Owner
              </Button>
            </div>
            {owners.length === 0 ? (
              <p className="text-center text-muted-foreground">No owners recorded</p>
            ) : (
              <div className="space-y-2">
                {owners.map((owner) => (
                  <div key={owner.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="grid grid-cols-2 gap-2 text-sm flex-1">
                        <div>
                          <span className="text-muted-foreground">Basis:</span> {owner.ownershipBasis}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Percentage:</span> {formatPercentage(owner.ownershipPercentage)}
                        </div>
                        {owner.notes && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Notes:</span> {owner.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEditOwner(owner)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteOwnerDialog({ open: true, owner })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      value: "valuations",
      label: `Valuations (${valuations.length})`,
      content: (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">Valuation History</h3>
              <Button size="sm" variant="outline" onClick={() => setValuationModalOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Valuation
              </Button>
            </div>
            {valuations.length === 0 ? (
              <p className="text-center text-muted-foreground">No valuations recorded</p>
            ) : (
              <div className="space-y-2">
                {valuations.map((valuation) => (
                  <div key={valuation.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="grid grid-cols-3 gap-2 text-sm flex-1">
                        <div>
                          <span className="text-muted-foreground">Date:</span> {formatDate(valuation.valuationDate)}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Amount:</span> {formatCurrency(valuation.valuationAmount)}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Source:</span> {valuation.valuationSource}
                        </div>
                        {valuation.notes && (
                          <div className="col-span-3">
                            <span className="text-muted-foreground">Notes:</span> {valuation.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEditValuation(valuation)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteValuationDialog({ open: true, valuation })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      value: "encumbrances",
      label: `Encumbrances (${encumbrances.length})`,
      content: (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">Encumbrances</h3>
              <Button size="sm" variant="outline" onClick={() => setEncumbranceModalOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Encumbrance
              </Button>
            </div>
            {encumbrances.length === 0 ? (
              <p className="text-center text-muted-foreground">No encumbrances recorded</p>
            ) : (
              <div className="space-y-2">
                {encumbrances.map((encumbrance) => (
                  <div key={encumbrance.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="grid grid-cols-3 gap-2 text-sm flex-1">
                        <div>
                          <span className="text-muted-foreground">Type:</span> {encumbrance.encumbranceType}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Amount:</span> {formatCurrency(encumbrance.encumbranceAmount)}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Holder:</span> {encumbrance.holderName || "-"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Registered:</span> {formatDate(encumbrance.registrationDate)}
                        </div>
                        {/* Discharge status badge — shown when dischargeRequired is true */}
                        {encumbrance.dischargeRequired && encumbrance.dischargeStatus && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground mr-1">Discharge:</span>
                            <DischargeStatusBadge
                              status={encumbrance.dischargeStatus}
                              encumbranceId={encumbrance.id}
                            />
                          </div>
                        )}
                        {encumbrance.notes && (
                          <div className="col-span-3">
                            <span className="text-muted-foreground">Notes:</span> {encumbrance.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`button-edit-encumbrance-${encumbrance.id}`}
                          onClick={() => handleEditEncumbrance(encumbrance)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteEncumbranceDialog({ open: true, encumbrance })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ),
    },
  ];

  // Add type-specific tab based on security type
  if (security.securityType === "residential_property" || security.securityType === "commercial_property") {
    tabs.splice(1, 0, {
      value: "property",
      label: "Property Details",
      content: (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold">Property-Specific Information</h3>
                {propertyData && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="button-edit-property"
                    onClick={() => setPropertyModalOpen(true)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              {isPropertyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : propertyData ? (
                <div className="space-y-6">
                  {/* Address Section */}
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Address</h4>
                    {propertyData.addressUnavailable ? (
                      <p className="text-sm text-muted-foreground italic">Address unavailable</p>
                    ) : (
                      <div className="text-sm space-y-1">
                        {propertyData.addressLine1 && <p>{propertyData.addressLine1}</p>}
                        {propertyData.addressLine2 && <p>{propertyData.addressLine2}</p>}
                        <p>
                          {[propertyData.suburb, propertyData.state, propertyData.postcode]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </p>
                        <p>{propertyData.country || "Australia"}</p>
                      </div>
                    )}
                  </div>

                  {/* Property Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Property Type</span>
                      <p className="text-sm font-medium">
                        {propertyData.isResidential
                          ? propertyData.propertyTypeResidential?.replace(/_/g, " ") || "Residential"
                          : propertyData.propertyTypeCommercial?.replace(/_/g, " ") || "Commercial"}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Status</span>
                      <p className="text-sm font-medium">{propertyData.propertyStatus?.replace(/_/g, " ") || "-"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Title Reference</span>
                      <p className="text-sm font-medium">{propertyData.titleReference || "-"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Land Area</span>
                      <p className="text-sm font-medium">{(propertyData as any).landAreaSqm ? `${(propertyData as any).landAreaSqm} sqm` : "-"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Building Area</span>
                      <p className="text-sm font-medium">{(propertyData as any).buildingAreaSqm ? `${(propertyData as any).buildingAreaSqm} sqm` : "-"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Year Built</span>
                      <p className="text-sm font-medium">{propertyData.yearBuilt || "-"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Zoning</span>
                      <p className="text-sm font-medium">{propertyData.zoning || "-"}</p>
                    </div>
                    {!propertyData.isResidential && (
                      <>
                        <div>
                          <span className="text-sm text-muted-foreground">Net Lettable Area</span>
                          <p className="text-sm font-medium">
                            {(propertyData as any).netLettableAreaSqm ? `${(propertyData as any).netLettableAreaSqm} sqm` : "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-muted-foreground">Passing Rent (Annual)</span>
                          <p className="text-sm font-medium">
                            {(propertyData as any).passingRentAnnual ? formatCurrency((propertyData as any).passingRentAnnual) : "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-muted-foreground">Capitalisation Rate</span>
                          <p className="text-sm font-medium">
                            {(propertyData as any).capitalisationRate ? formatPercentage((propertyData as any).capitalisationRate) : "-"}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground text-sm mb-4">No property details recorded yet.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="button-add-property"
                    onClick={() => setPropertyModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Property Details
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Property Titles Panel — embedded below property details */}
          <PropertyTitlesPanel securityId={securityId!} />
        </div>
      ),
    });

    // Add Tenancies tab only for commercial properties (after Property Details)
    if (security.securityType === "commercial_property") {
      tabs.splice(2, 0, {
        value: "tenancies",
        label: "Tenancies",
        content: <SecurityTenanciesTab securityId={securityId!} />,
      });

      // Add WALE Analysis tab after Tenancies (index 3)
      tabs.splice(3, 0, {
        value: "wale",
        label: "WALE Analysis",
        content: (
          <WaleDisplayCard
            securityId={securityId!}
            isActive={activeTab === "wale"}
          />
        ),
      });

      // Add Cashflow Projections tab after WALE (index 4)
      tabs.splice(4, 0, {
        value: "cashflow",
        label: "Cashflow",
        content: <CashflowProjectionsPanel securityId={securityId!} />,
      });
    }
  } else if (security.securityType === "vehicle") {
    tabs.splice(1, 0, {
      value: "vehicle",
      label: "Vehicle Details",
      content: <VehicleDetailTab securityId={securityId!} />,
    });
  } else if (security.securityType === "equipment") {
    tabs.splice(1, 0, {
      value: "equipment",
      label: "Equipment Details",
      content: <EquipmentDetailTab securityId={securityId!} />,
    });
  } else if (security.securityType === "financial_asset") {
    tabs.splice(1, 0, {
      value: "financial",
      label: "Financial Asset Details",
      content: <FinancialAssetDetailTab securityId={securityId!} />,
    });
  }

  return (
    <DetailPageShell
      title={securityLabel}
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Securities", href: "/securities" },
        { label: securityLabel },
      ]}
      value={activeTab}
      onValueChange={setActiveTab}
      badge={
        <Badge className={getStatusBadgeColor(security.status)}>
          {STATUS_OPTIONS.find((s) => s.value === security.status)?.label || security.status}
        </Badge>
      }
      navItems={tabs.map((tab) => ({
        label: tab.label,
        href: `#${tab.value}`,
      }))}
      tabs={tabs}
      rightPanel={
        <div className="space-y-4">
          {/* Key Information */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY INFORMATION</h3>
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-muted-foreground">Type</p>
                <p className="font-medium">{SECURITY_TYPE_OPTIONS.find(t => t.value === security.securityType)?.label}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Estimated Value</p>
                <p className="font-medium">{formatCurrency(security.estimatedValue)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Valuation Date</p>
                <p className="font-medium">{formatDate(security.valuationDate)}</p>
              </div>
            </div>
          </div>

          {/* Key Dates */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">{formatDate(security.createdAt)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Modified</p>
                <p className="font-medium">{formatDate(security.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">QUICK STATS</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Owners:</span>
                <span className="font-medium">{owners.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valuations:</span>
                <span className="font-medium">{valuations.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Encumbrances:</span>
                <span className="font-medium">{encumbrances.length}</span>
              </div>
            </div>
          </div>
        </div>
      }
    >
      {/* Modals */}
      <SecurityOwnerFormModal
        open={ownerModalOpen}
        onOpenChange={handleOwnerModalClose}
        securityId={securityId!}
        owner={editingOwner}
      />
      <ValuationFormModal
        open={valuationModalOpen}
        onOpenChange={handleValuationModalClose}
        securityId={securityId!}
        valuation={editingValuation}
      />
      <EncumbranceFormModal
        open={encumbranceModalOpen}
        onOpenChange={handleEncumbranceModalClose}
        securityId={securityId!}
        encumbrance={editingEncumbrance}
      />
      {isPropertySecurity && (
        <PropertyFormModal
          open={propertyModalOpen}
          onOpenChange={setPropertyModalOpen}
          securityId={securityId!}
          existingProperty={propertyData || undefined}
        />
      )}

      {/* Delete Confirmation Dialogs */}
      <AlertDialog
        open={deleteOwnerDialog.open}
        onOpenChange={(open) => setDeleteOwnerDialog({ open, owner: open ? deleteOwnerDialog.owner : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Owner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this owner? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteOwnerDialog.owner && deleteOwnerMutation.mutate(deleteOwnerDialog.owner.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteOwnerMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteValuationDialog.open}
        onOpenChange={(open) => setDeleteValuationDialog({ open, valuation: open ? deleteValuationDialog.valuation : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Valuation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this valuation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteValuationDialog.valuation && deleteValuationMutation.mutate(deleteValuationDialog.valuation.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteValuationMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteEncumbranceDialog.open}
        onOpenChange={(open) => setDeleteEncumbranceDialog({ open, encumbrance: open ? deleteEncumbranceDialog.encumbrance : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Encumbrance</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this encumbrance? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEncumbranceDialog.encumbrance && deleteEncumbranceMutation.mutate(deleteEncumbranceDialog.encumbrance.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteEncumbranceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DetailPageShell>
  );
}
